// src/services/classRecordings.js
// Firestore helpers for on-demand class recordings (VOD assets from Mux)
// Collection: classRecordings
// Schema: {
//   muxAssetId, muxPlaybackId, title, description, duration (min), durationSeconds,
//   thumbnailUrl, classId, classPrice, hlsUrl, easeIn, strongPlus, isFree,
//   likeCount, commentCount, createdAt, status
// }

import { db, firebaseEnabled } from './firebase';
import { collection, getDocs, query, orderBy, doc } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { updateDoc } from './firestoreWrite';

// ── Update thumbnail URL on a class recording ──
export async function updateClassRecordingThumbnail(recordingId, thumbnailUrl) {
    if (!firebaseEnabled || !db || !recordingId) return false;
    try {
        await updateDoc(doc(db, 'classRecordings', recordingId), { thumbnailUrl });
        return true;
    } catch (e) {
        console.error('Error updating recording thumbnail:', e);
        return false;
    }
}

// ── Mark a recording as free (admin-toggled) ──
// When true, all users — including non-subscribers — can watch this recording.
// Used for "try before you buy" recordings that bypass the subscription paywall.
export async function setClassRecordingFree(recordingId, isFree) {
    if (!firebaseEnabled || !db || !recordingId) return false;
    try {
        await updateDoc(doc(db, 'classRecordings', recordingId), { isFree: !!isFree });
        return true;
    } catch (e) {
        console.error('Error updating recording free flag:', e);
        return false;
    }
}

// ── Auto-detect muscle groups from title keywords ──
// Same logic as bunnyStream.js — keeps filters working even when admin doesn't tag
function detectGroupsFromTitle(title) {
    if (!title) return [];
    const t = title.toLowerCase();
    const groups = [];
    if (/\bupper\b|\barms?\b|\bshoulder|bicep|tricep|chest|back\b|\bbatwing/i.test(t)) groups.push('upper');
    if (/\blower\b|\blegs?\b|\bglute|squat|lunge|calf|calves|hamstring|quad\b/i.test(t)) groups.push('lower');
    if (/\bcore\b|\babs?\b|\bplank|oblique\b/i.test(t)) groups.push('core');
    if (/\bstretch\b|\bstretching\b|\bflexibility\b|\bmobility\b|\byoga\b|\bcool\s*down\b|\bwarm\s*up\b|\brecovery\b/i.test(t)) groups.push('stretch');
    return groups;
}

// ── Fetch all class recordings (for On Demand tab) ──
export async function fetchClassRecordings() {
    if (!firebaseEnabled || !db) return [];
    try {
        const q = query(collection(db, 'classRecordings'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => {
            const data = d.data();
            // Prefer admin-set groups from Firestore, fall back to auto-detect from title
            const groups = (data.groups && data.groups.length > 0)
                ? data.groups
                : detectGroupsFromTitle(data.title);

            // Duration: `durationSeconds` is the authoritative value written directly from Mux's
            // `asset.duration`. The `duration` field (minutes) is derived from it at save time, but
            // some older records were backfilled from the *scheduled* class duration when the real
            // Mux duration hadn't propagated yet — so the two can disagree. When they do, prefer
            // durationSeconds. Also rounds to the nearest minute here rather than trusting whatever
            // `duration` was written as, so the badge stays consistent with actual video length.
            let displayMinutes = data.duration || 0;
            if (data.durationSeconds && data.durationSeconds > 30) {
                const mxMinutes = Math.round(data.durationSeconds / 60);
                // If stored `duration` clearly disagrees with durationSeconds (diff > 1 min), trust Mux
                if (Math.abs(mxMinutes - displayMinutes) > 1) {
                    displayMinutes = mxMinutes;
                } else if (!displayMinutes) {
                    displayMinutes = mxMinutes;
                }
            }

            return {
                id: d.id,
                ...data,
                // Normalize to match the video object shape used by VideoCard.
                // `isFree` reads admin-toggled flag from Firestore (defaults to false
                // when unset — preserves prior behavior for every recording that
                // hasn't been explicitly marked free).
                isFree: data.isFree === true,
                isClassRecording: true,
                groups,
                easeIn: data.easeIn || false,
                strongPlus: data.strongPlus || false,
                description: data.description || '',
                length: displayMinutes,
                streamUrl: null, // Not a Bunny video
                driveThumbnail: data.thumbnailUrl || null,
            };
        });
    } catch (e) {
        console.error('Error fetching class recordings:', e);
        return [];
    }
}
